import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToUserSavedCards,
  cleanupWalletListeners,
} from "../../redux/transactionsSlice";
import axios from "axios";
import logo from "../../assets/cosmoola/logo.jpg";
import countries, { CountryInfo } from "../../GLOBAL/Country";
import {
  ArrowLeft, CreditCard, Trash2, Star, Plus, X, CheckCircle,
  AlertCircle, Shield, Loader2, Building2, Smartphone, ChevronRight,
  ChevronDown, Lock, Globe, Copy, Check, Phone, RefreshCw, Wallet,
} from "lucide-react";

/* ─── STRIPE ─── */
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

/* ─── THEME ─── */
const D = {
  bg: "#000000", surface: "#0a0a0a", surface2: "#101010", border: "#1c1c1c",
  border2: "#2a2a2a", white: "#ffffff", offwhite: "#e8e8e8", muted: "#6b6b6b",
  subtle: "#3a3a3a", gold: "#C9A96E",
};
const L = {
  bg: "#ffffff", surface: "#fafafa", surface2: "#f3f3f3", border: "#e5e5e5",
  border2: "#d4d4d4", text: "#0a0a0a", textSoft: "#525252", muted: "#a3a3a3",
  subtle: "#d4d4d4",
};

const API = "https://europe-west3-cosmoola.cloudfunctions.net";

/* ─── CARD BRAND COLORS ─── */
const BRAND_COLORS: Record<string, { bg: string; accent: string; label: string }> = {
  visa:       { bg: "#1a1f71", accent: "#f7a600", label: "VISA" },
  mastercard: { bg: "#252525", accent: "#eb001b", label: "MC"   },
  amex:       { bg: "#007bc1", accent: "#ffffff", label: "AMEX" },
  discover:   { bg: "#ff6600", accent: "#ffffff", label: "DISC" },
  default:    { bg: "#1a1a1a", accent: D.gold,    label: "CARD" },
};

/* ─── BANK TRANSFER DATA ─── */
const BANK_DETAILS = {
  local: {
    bankName: "Cosmoola Banking Partner",
    accountHolder: "Cosmoola Technologies OÜ",
    iban: "EE38 2200 2210 2014 5685",
    bic: "HABAEE2X",
  },
  international: {
    accountHolder: "Cosmoola Technologies OÜ",
    iban: "EE38 2200 2210 2014 5685",
    bic: "HABAEE2X",
    correspondentBank: "Deutsche Bank AG",
    correspondentBIC: "DEUTDEDB",
  },
};

/* ═══════════════════════════════════════════════════════════
   MOBILE MONEY — country → provider mapping
   Only countries that have a real provider in the backend.
   currency must match what the backend MOBILE_PROVIDERS expects.
════════════════════════════════════════════════════════════ */
type MobileProvider = {
  key: string;
  label: string;
  currency: string;
  currencySymbol: string;
  minAmount: number;
  maxAmount: number;
  flag: string;
};

type MobileCountryEntry = {
  countryCode: string;   // ISO-2
  phoneCode: string;     // e.g. "+254"
  providers: MobileProvider[];
};

const MOBILE_COUNTRIES: MobileCountryEntry[] = [
  {
    countryCode: "KE", phoneCode: "+254",
    providers: [
      { key: "mpesa_ke",  label: "M-Pesa",        currency: "KES", currencySymbol: "KSh", minAmount: 10,  maxAmount: 150000, flag: "🇰🇪" },
      { key: "airtel_ke", label: "Airtel Money",   currency: "KES", currencySymbol: "KSh", minAmount: 10,  maxAmount: 70000,  flag: "🇰🇪" },
    ],
  },
  {
    countryCode: "TZ", phoneCode: "+255",
    providers: [
      { key: "mpesa_tz", label: "M-Pesa Tanzania", currency: "TZS", currencySymbol: "TSh", minAmount: 500,  maxAmount: 5000000, flag: "🇹🇿" },
      { key: "tigo_tz",  label: "Tigo Pesa",       currency: "TZS", currencySymbol: "TSh", minAmount: 500,  maxAmount: 3000000, flag: "🇹🇿" },
    ],
  },
  {
    countryCode: "UG", phoneCode: "+256",
    providers: [
      { key: "mpesa_ug",  label: "M-Pesa Uganda",  currency: "UGX", currencySymbol: "USh", minAmount: 500,  maxAmount: 7000000, flag: "🇺🇬" },
      { key: "mtn_ug",    label: "MTN Mobile Money",currency: "UGX", currencySymbol: "USh", minAmount: 500,  maxAmount: 7000000, flag: "🇺🇬" },
      { key: "airtel_ug", label: "Airtel Money",    currency: "UGX", currencySymbol: "USh", minAmount: 500,  maxAmount: 5000000, flag: "🇺🇬" },
    ],
  },
  {
    countryCode: "GH", phoneCode: "+233",
    providers: [
      { key: "mtn_gh",      label: "MTN MoMo",       currency: "GHS", currencySymbol: "GH₵", minAmount: 1,   maxAmount: 10000,  flag: "🇬🇭" },
      { key: "vodafone_gh", label: "Vodafone Cash",  currency: "GHS", currencySymbol: "GH₵", minAmount: 1,   maxAmount: 10000,  flag: "🇬🇭" },
    ],
  },
  {
    countryCode: "NG", phoneCode: "+234",
    providers: [
      { key: "momo_ng", label: "MoMo Nigeria", currency: "NGN", currencySymbol: "₦", minAmount: 50, maxAmount: 1000000, flag: "🇳🇬" },
    ],
  },
  {
    countryCode: "SN", phoneCode: "+221",
    providers: [
      { key: "orange_sn", label: "Orange Money", currency: "XOF", currencySymbol: "CFA", minAmount: 500, maxAmount: 2000000, flag: "🇸🇳" },
    ],
  },
  {
    countryCode: "ZW", phoneCode: "+263",
    providers: [
      { key: "ecocash_zw", label: "EcoCash", currency: "ZWL", currencySymbol: "Z$", minAmount: 1, maxAmount: 500000, flag: "🇿🇼" },
    ],
  },
  {
    countryCode: "MZ", phoneCode: "+258",
    providers: [
      { key: "mpesa_mz", label: "M-Pesa Mozambique", currency: "MZN", currencySymbol: "MT", minAmount: 10, maxAmount: 100000, flag: "🇲🇿" },
    ],
  },
  {
    countryCode: "PK", phoneCode: "+92",
    providers: [
      { key: "jazzcash_pk", label: "JazzCash", currency: "PKR", currencySymbol: "₨", minAmount: 100, maxAmount: 200000, flag: "🇵🇰" },
    ],
  },
  {
    countryCode: "BD", phoneCode: "+880",
    providers: [
      { key: "bkash_bd", label: "bKash", currency: "BDT", currencySymbol: "৳", minAmount: 10, maxAmount: 200000, flag: "🇧🇩" },
    ],
  },
];

/* Countries that have mobile money — for the dropdown filter */
const MOBILE_COUNTRY_CODES = new Set(MOBILE_COUNTRIES.map(e => e.countryCode));

/* Get country info from the shared countries list */
const getCountryInfo = (code: string): CountryInfo | undefined =>
  countries.find(c => c.code === code);

/* ═══════════════════════════════════════════════════════════
   MOBILE MONEY PANEL
════════════════════════════════════════════════════════════ */
type MobileStep = "form" | "pending" | "success" | "failed";

const MobileMoney: React.FC<{ userId: string }> = ({ userId }) => {
  /* Country + provider selection */
  const [selectedCountryCode, setSelectedCountryCode] = useState("KE");
  const [countryOpen, setCountryOpen]                 = useState(false);
  const [providerKey, setProviderKey]                 = useState("mpesa_ke");
  const [phoneDigits, setPhoneDigits]                 = useState("");
  const [localAmount, setLocalAmount]                 = useState("");
  const [loading, setLoading]                         = useState(false);
  const [step, setStep]                               = useState<MobileStep>("form");
  const [txnId, setTxnId]                             = useState<string | null>(null);
  const [creditEUR, setCreditEUR]                     = useState<number | null>(null);
  const [feeEUR, setFeeEUR]                           = useState<number | null>(null);
  const [fxRate, setFxRate]                           = useState<number | null>(null);
  const [grossEUR, setGrossEUR]                       = useState<number | null>(null);
  const [errorMsg, setErrorMsg]                       = useState("");
  const [pollCount, setPollCount]                     = useState(0);
  const [countrySearch, setCountrySearch]             = useState("");
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const countryEntry   = MOBILE_COUNTRIES.find(e => e.countryCode === selectedCountryCode)!;
  const provider       = countryEntry.providers.find(p => p.key === providerKey) ?? countryEntry.providers[0];
  const countryInfo    = getCountryInfo(selectedCountryCode);
  const phoneCode      = countryEntry.phoneCode;

  /* Filter countries list to only mobile-money ones */
  const mobileCountries = countries.filter(c => MOBILE_COUNTRY_CODES.has(c.code));
  const filteredCountries = countrySearch
    ? mobileCountries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()))
    : mobileCountries;

  /* When country changes, reset to first provider */
  const handleCountrySelect = (code: string) => {
    setSelectedCountryCode(code);
    const entry = MOBILE_COUNTRIES.find(e => e.countryCode === code)!;
    setProviderKey(entry.providers[0].key);
    setCountryOpen(false);
    setCountrySearch("");
    setPhoneDigits("");
  };

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setCountryOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Poll for transaction status */
  const startPolling = useCallback((id: string) => {
    let count = 0;
    pollRef.current = setInterval(async () => {
      count++;
      setPollCount(count);
      try {
        const { data } = await axios.post(`${API}/checkMobileTopUpStatus`, { transactionId: id });
        if (data.status === "succeeded") {
          clearInterval(pollRef.current!);
          setStep("success");
          setCreditEUR(data.walletCreditEUR);
        } else if (data.status === "failed") {
          clearInterval(pollRef.current!);
          setStep("failed");
          setErrorMsg(data.errorMessage || "Payment was declined or cancelled");
        } else if (count >= 40) {
          // 40 × 3s = 2 min timeout
          clearInterval(pollRef.current!);
          setStep("failed");
          setErrorMsg("Payment timed out. If you were charged, contact support.");
        }
      } catch (e) {
        // network blip — keep polling
      }
    }, 3000);
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const fmt = (n: number, sym: string) =>
    sym + n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handlePay = async () => {
    const amount = Number(localAmount);
    if (!amount || amount < provider.minAmount || amount > provider.maxAmount) {
      setErrorMsg(`Amount must be between ${provider.currencySymbol}${provider.minAmount} and ${provider.currencySymbol}${provider.maxAmount.toLocaleString()}`);
      return;
    }
    if (!phoneDigits || phoneDigits.length < 6) {
      setErrorMsg("Please enter a valid phone number");
      return;
    }

    setErrorMsg("");
    setLoading(true);

    // Build full international phone number
    const fullPhone = phoneCode + phoneDigits.replace(/^0/, "");

    try {
      const { data } = await axios.post(`${API}/initiateMobileTopUp`, {
        userId,
        provider:    providerKey,
        phone:       fullPhone,
        localAmount: amount,
      });

      setTxnId(data.transactionId);
      setCreditEUR(data.walletCreditEUR);
      setFeeEUR(data.ourServiceFeeEUR);
      setFxRate(data.fxRate);
      setGrossEUR(data.grossAmountEUR);
      setStep("pending");
      startPolling(data.transactionId);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || "Failed to initiate payment";
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setStep("form");
    setTxnId(null);
    setPhoneDigits("");
    setLocalAmount("");
    setErrorMsg("");
    setPollCount(0);
    setCreditEUR(null);
  };

  /* ── FORM ── */
  if (step === "form") return (
    <div style={{ background: L.surface, border: `1px solid ${L.border}`, padding: "28px 24px" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${L.border}` }}>
        <div style={{ width: 44, height: 44, background: D.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Smartphone size={20} color={D.gold} strokeWidth={1.5} />
        </div>
        <div>
          <p style={{ fontSize: 13, fontWeight: 600, color: L.text, letterSpacing: "0.06em" }}>Mobile Money</p>
          <p style={{ fontSize: 10, color: L.muted, marginTop: 2 }}>M-Pesa, Airtel, MTN, bKash & more · Instant STK push</p>
        </div>
      </div>

      {/* Country selector */}
      <div style={{ marginBottom: 16 }} ref={dropRef}>
        <label style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", display: "block", marginBottom: 8 }}>COUNTRY</label>
        <div
          onClick={() => setCountryOpen(v => !v)}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: L.surface2, border: `1px solid ${countryOpen ? L.text : L.border}`,
            padding: "13px 16px", cursor: "pointer", transition: "border-color 0.2s",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>{countryInfo?.emoji ?? "🌍"}</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: L.text }}>{countryInfo?.name ?? selectedCountryCode}</span>
            <span style={{ fontSize: 10, color: L.muted }}>{phoneCode}</span>
          </div>
          <ChevronDown size={14} color={L.muted} style={{ transform: countryOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </div>

        <AnimatePresence>
          {countryOpen && (
            <motion.div
              initial={{ opacity: 0, y: -4, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -4, height: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: "hidden", border: `1px solid ${L.border}`, borderTop: "none", background: L.bg, zIndex: 50, position: "relative" }}
            >
              <div style={{ padding: "10px 12px", borderBottom: `1px solid ${L.border}` }}>
                <input
                  autoFocus
                  placeholder="Search countries..."
                  value={countrySearch}
                  onChange={e => setCountrySearch(e.target.value)}
                  style={{
                    width: "100%", background: L.surface2, border: `1px solid ${L.border}`,
                    padding: "9px 12px", fontSize: 12, color: L.text, outline: "none",
                    fontFamily: "'Montserrat',sans-serif",
                  }}
                />
              </div>
              <div style={{ maxHeight: 240, overflowY: "auto" }}>
                {filteredCountries.map(c => {
                  const entry = MOBILE_COUNTRIES.find(e => e.countryCode === c.code)!;
                  return (
                    <div
                      key={c.code}
                      onClick={() => handleCountrySelect(c.code)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "12px 16px", cursor: "pointer",
                        background: selectedCountryCode === c.code ? L.surface : "transparent",
                        borderBottom: `1px solid ${L.border}`,
                        transition: "background 0.15s",
                      }}
                    >
                      <span style={{ fontSize: 18 }}>{c.emoji}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: L.text }}>{c.name}</div>
                        <div style={{ fontSize: 10, color: L.muted, marginTop: 2 }}>
                          {entry.providers.map(p => p.label).join(" · ")}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, color: L.muted }}>{c.phoneCode}</span>
                    </div>
                  );
                })}
                {filteredCountries.length === 0 && (
                  <div style={{ padding: "20px", textAlign: "center", fontSize: 12, color: L.muted }}>
                    No mobile money countries match "{countrySearch}"
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Provider selector (shown only when country has multiple) */}
      {countryEntry.providers.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", display: "block", marginBottom: 8 }}>PROVIDER</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {countryEntry.providers.map(p => (
              <button
                key={p.key}
                onClick={() => setProviderKey(p.key)}
                style={{
                  padding: "8px 16px",
                  border: providerKey === p.key ? `2px solid ${L.text}` : `1px solid ${L.border}`,
                  background: providerKey === p.key ? L.text : "transparent",
                  color: providerKey === p.key ? L.bg : L.text,
                  cursor: "pointer", fontSize: 11, fontWeight: 500,
                  fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.05em",
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Phone number input */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", display: "block", marginBottom: 8 }}>MOBILE NUMBER</label>
        <div style={{ display: "flex", gap: 0 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "0 14px", background: L.surface2,
            border: `1px solid ${L.border}`, borderRight: "none",
            fontSize: 13, color: L.textSoft, flexShrink: 0,
          }}>
            <span style={{ fontSize: 16 }}>{countryInfo?.emoji}</span>
            <span style={{ fontWeight: 500 }}>{phoneCode}</span>
          </div>
          <input
            type="tel"
            placeholder="712 345 678"
            value={phoneDigits}
            onChange={e => setPhoneDigits(e.target.value.replace(/[^0-9]/g, ""))}
            style={{
              flex: 1, background: L.surface2, border: `1px solid ${L.border}`,
              padding: "13px 14px", color: L.text, fontSize: 14,
              fontFamily: "'Montserrat',sans-serif", outline: "none",
            }}
          />
        </div>
        <p style={{ fontSize: 10, color: L.muted, marginTop: 5 }}>
          Enter without country code — we'll add {phoneCode} automatically
        </p>
      </div>

      {/* Amount input */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", display: "block", marginBottom: 8 }}>
          AMOUNT ({provider.currency})
        </label>
        <div style={{ display: "flex", gap: 0 }}>
          <div style={{
            display: "flex", alignItems: "center",
            padding: "0 14px", background: L.surface2,
            border: `1px solid ${L.border}`, borderRight: "none",
            fontSize: 13, fontWeight: 600, color: L.textSoft, flexShrink: 0,
          }}>
            {provider.currencySymbol}
          </div>
          <input
            type="number"
            min={provider.minAmount}
            max={provider.maxAmount}
            placeholder={`Min ${provider.minAmount.toLocaleString()}`}
            value={localAmount}
            onChange={e => setLocalAmount(e.target.value)}
            style={{
              flex: 1, background: L.surface2, border: `1px solid ${L.border}`,
              padding: "13px 14px", color: L.text, fontSize: 14,
              fontFamily: "'Montserrat',sans-serif", outline: "none",
            }}
          />
        </div>
        <p style={{ fontSize: 10, color: L.muted, marginTop: 5 }}>
          Min {provider.currencySymbol}{provider.minAmount.toLocaleString()} · Max {provider.currencySymbol}{provider.maxAmount.toLocaleString()} · Converted to EUR at live rate
        </p>
      </div>

      {/* Quick amounts */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
        {(() => {
          const steps = provider.minAmount >= 500
            ? [500, 1000, 2000, 5000, 10000]
            : provider.minAmount >= 50
            ? [50, 100, 200, 500, 1000]
            : [10, 50, 100, 500, 1000];
          return steps
            .filter(s => s <= provider.maxAmount)
            .map(s => (
              <button
                key={s}
                onClick={() => setLocalAmount(String(s))}
                style={{
                  padding: "7px 14px",
                  border: localAmount === String(s) ? `1px solid ${L.text}` : `1px solid ${L.border}`,
                  background: localAmount === String(s) ? L.text : "transparent",
                  color: localAmount === String(s) ? L.bg : L.textSoft,
                  cursor: "pointer", fontSize: 11,
                  fontFamily: "'Montserrat',sans-serif",
                }}
              >
                {provider.currencySymbol}{s.toLocaleString()}
              </button>
            ));
        })()}
      </div>

      {/* Error */}
      {errorMsg && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: "#fff0f0", border: "1px solid #f0c8c8", marginBottom: 16 }}>
          <AlertCircle size={13} color="#B22222" />
          <span style={{ fontSize: 11, color: "#B22222" }}>{errorMsg}</span>
        </div>
      )}

      {/* Security note */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: "#f0f9f0", border: "1px solid #d4ecd4", marginBottom: 16 }}>
        <Lock size={12} color="#2d7a2d" />
        <span style={{ fontSize: 10, color: "#2d7a2d", lineHeight: 1.6 }}>
          A payment prompt will be sent to your phone. Enter your {provider.label} PIN to confirm. Cosmoola never sees your PIN.
        </span>
      </div>

      {/* Pay button */}
      <motion.button
        onClick={handlePay}
        disabled={loading}
        whileTap={{ scale: 0.97 } as any}
        style={{
          width: "100%", padding: "15px 0",
          background: loading ? L.border : D.bg,
          border: "none", color: loading ? L.muted : D.white,
          cursor: loading ? "not-allowed" : "pointer",
          fontSize: 11, fontWeight: 600, letterSpacing: "0.2em",
          fontFamily: "'Montserrat',sans-serif",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}
      >
        {loading
          ? <><Loader2 size={14} className="pm-spin" /> SENDING PROMPT...</>
          : <><Smartphone size={14} /> PAY WITH {provider.label.toUpperCase()}</>}
      </motion.button>
    </div>
  );

  /* ── PENDING / POLLING ── */
  if (step === "pending") return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      style={{ background: L.surface, border: `1px solid ${L.border}`, padding: "48px 28px", textAlign: "center" }}
    >
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#fffbf0", border: "1px solid #f0e8c8", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
        <Smartphone size={28} color="#b8860b" />
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 300, fontFamily: "'Cormorant Garamond',serif", color: L.text, marginBottom: 8 }}>
        Check Your Phone
      </h3>
      <p style={{ fontSize: 12, color: L.textSoft, lineHeight: 1.8, maxWidth: 340, margin: "0 auto 24px" }}>
        A {provider.label} payment prompt has been sent to <strong>{phoneCode}{phoneDigits}</strong>. Enter your PIN to complete the payment.
      </p>

      {/* Fee breakdown */}
      {grossEUR && (
        <div style={{ background: L.surface2, border: `1px solid ${L.border}`, padding: "16px", marginBottom: 24, textAlign: "left" }}>
          <p style={{ fontSize: 9, color: L.muted, letterSpacing: "0.2em", marginBottom: 12 }}>PAYMENT SUMMARY</p>
          {[
            { label: "You send",    value: `${provider.currencySymbol}${Number(localAmount).toLocaleString()} ${provider.currency}` },
            { label: "FX rate",     value: `1 ${provider.currency} = €${fxRate?.toFixed(5)}` },
            { label: "EUR value",   value: `€${grossEUR.toFixed(2)}` },
            { label: "Service fee", value: `€${feeEUR?.toFixed(2)}` },
            { label: "Wallet credit", value: `€${creditEUR?.toFixed(2)}` },
          ].map(r => (
            <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${L.border}` }}>
              <span style={{ fontSize: 11, color: L.textSoft }}>{r.label}</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: r.label === "Wallet credit" ? L.text : L.textSoft }}>{r.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Pulse indicator */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 20 }}>
        <Loader2 size={14} color={L.muted} className="pm-spin" />
        <span style={{ fontSize: 11, color: L.muted }}>
          Waiting for confirmation{".".repeat((pollCount % 3) + 1)}
        </span>
      </div>

      <button
        onClick={reset}
        style={{
          background: "transparent", border: `1px solid ${L.border}`,
          padding: "10px 24px", cursor: "pointer", color: L.textSoft,
          fontSize: 11, letterSpacing: "0.1em", fontFamily: "'Montserrat',sans-serif",
        }}
      >
        Cancel
      </button>
    </motion.div>
  );

  /* ── SUCCESS ── */
  if (step === "success") return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
      style={{ background: L.surface, border: `1px solid ${L.border}`, padding: "48px 28px", textAlign: "center" }}
    >
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#f0f9f0", border: "1px solid #d4ecd4", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
        <CheckCircle size={28} color="#2d7a2d" />
      </div>
      <h3 style={{ fontSize: 22, fontWeight: 300, fontFamily: "'Cormorant Garamond',serif", color: L.text, marginBottom: 8 }}>
        Payment Confirmed
      </h3>
      <p style={{ fontSize: 13, color: L.textSoft, marginBottom: 6 }}>
        <strong style={{ color: L.text, fontFamily: "'Cormorant Garamond',serif", fontSize: 20 }}>
          €{creditEUR?.toFixed(2)}
        </strong>
        {" "}has been added to your Cosmoola wallet.
      </p>
      <p style={{ fontSize: 11, color: L.muted, marginBottom: 28 }}>via {provider.label} · {provider.currency}</p>
      <motion.button
        onClick={reset}
        whileTap={{ scale: 0.97 } as any}
        style={{
          background: D.bg, color: D.white, border: "none",
          padding: "12px 32px", cursor: "pointer",
          fontSize: 11, fontWeight: 600, letterSpacing: "0.2em",
          fontFamily: "'Montserrat',sans-serif",
          display: "inline-flex", alignItems: "center", gap: 8,
        }}
      >
        <Wallet size={14} /> TOP UP AGAIN
      </motion.button>
    </motion.div>
  );

  /* ── FAILED ── */
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      style={{ background: L.surface, border: `1px solid ${L.border}`, padding: "48px 28px", textAlign: "center" }}
    >
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#fff0f0", border: "1px solid #f0c8c8", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
        <AlertCircle size={28} color="#B22222" />
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 300, fontFamily: "'Cormorant Garamond',serif", color: L.text, marginBottom: 8 }}>Payment Failed</h3>
      <p style={{ fontSize: 11, color: L.textSoft, lineHeight: 1.7, maxWidth: 320, margin: "0 auto 24px" }}>{errorMsg}</p>
      <button
        onClick={reset}
        style={{
          background: D.bg, color: D.white, border: "none",
          padding: "12px 28px", cursor: "pointer",
          fontSize: 11, fontWeight: 600, letterSpacing: "0.2em",
          fontFamily: "'Montserrat',sans-serif",
          display: "inline-flex", alignItems: "center", gap: 8,
        }}
      >
        <RefreshCw size={13} /> TRY AGAIN
      </button>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════
   STRIPE CARD FORM
════════════════════════════════════════════════════════════ */
const STRIPE_ELEMENT_STYLE = {
  style: {
    base: {
      color: L.text, fontFamily: "'Montserrat', sans-serif",
      fontSmoothing: "antialiased", fontSize: "14px", letterSpacing: "0.03em",
      "::placeholder": { color: L.muted },
    },
    invalid: { color: "#B22222", iconColor: "#B22222" },
  },
};

interface CardFormProps {
  userId: string; email: string; name: string;
  onSuccess: (msg: string) => void; onError: (msg: string) => void; onClose: () => void;
}

const CardForm: React.FC<CardFormProps> = ({ userId, email, name, onSuccess, onError, onClose }) => {
  const stripe    = useStripe();
  const elements  = useElements();
  const [loading, setLoading]         = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [cardholderName, setCardholderName] = useState(name);
  const [focusedField, setFocusedField]     = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    try {
      setLoadingStep("Setting up account...");
      await axios.post(`${API}/createStripeUser`, { userId, email, name: cardholderName || name });

      setLoadingStep("Preparing secure form...");
      const { data: intentData } = await axios.post(`${API}/createSetupIntent`, { userId });
      if (!intentData.clientSecret) throw new Error("Failed to create setup intent");

      setLoadingStep("Verifying card...");
      const { error, setupIntent } = await stripe.confirmCardSetup(intentData.clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement)!,
          billing_details: { name: cardholderName || name || undefined, email },
        },
      });
      if (error) throw new Error(error.message);
      if (!setupIntent?.payment_method) throw new Error("No payment method returned");

      setLoadingStep("Saving card...");
      const { data: saveData } = await axios.post(`${API}/saveCardAfterSetup`, {
        userId, paymentMethodId: setupIntent.payment_method,
      });
      onSuccess(`${saveData.brand?.toUpperCase()} •••• ${saveData.last4} added successfully`);
      onClose();
    } catch (err: any) {
      onError(err.response?.data?.error || err.message || "Failed to add card");
    } finally { setLoading(false); setLoadingStep(""); }
  };

  const fieldStyle = (field: string) => ({
    background: L.surface2,
    border: `1px solid ${focusedField === field ? L.text : L.border}`,
    padding: "14px 16px", transition: "border-color 0.2s",
  });

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <label style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", display: "block", marginBottom: 8 }}>CARDHOLDER NAME</label>
        <input type="text" placeholder="As printed on card" value={cardholderName}
          onChange={e => setCardholderName(e.target.value)}
          onFocus={() => setFocusedField("name")} onBlur={() => setFocusedField(null)}
          style={{ width: "100%", background: L.surface2, border: `1px solid ${focusedField === "name" ? L.text : L.border}`, padding: "14px 16px", color: L.text, fontSize: 14, fontFamily: "'Montserrat',sans-serif", outline: "none", transition: "border-color 0.2s" }}
        />
      </div>
      <div>
        <label style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", display: "block", marginBottom: 8 }}>CARD NUMBER</label>
        <div style={fieldStyle("number")} onClick={() => setFocusedField("number")}>
          <CardNumberElement options={{ ...STRIPE_ELEMENT_STYLE, showIcon: true }} onFocus={() => setFocusedField("number")} onBlur={() => setFocusedField(null)} />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", display: "block", marginBottom: 8 }}>EXPIRY DATE</label>
          <div style={fieldStyle("expiry")}><CardExpiryElement options={STRIPE_ELEMENT_STYLE} onFocus={() => setFocusedField("expiry")} onBlur={() => setFocusedField(null)} /></div>
        </div>
        <div>
          <label style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", display: "block", marginBottom: 8 }}>CVC / CVV</label>
          <div style={fieldStyle("cvc")}><CardCvcElement options={STRIPE_ELEMENT_STYLE} onFocus={() => setFocusedField("cvc")} onBlur={() => setFocusedField(null)} /></div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: "#f0f9f0", border: "1px solid #d4ecd4" }}>
        <Lock size={12} color="#2d7a2d" />
        <span style={{ fontSize: 10, color: "#2d7a2d", letterSpacing: "0.05em" }}>Your card is encrypted and stored securely by Stripe. Cosmoola never sees your full card details.</span>
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
        <button type="button" onClick={onClose} style={{ flex: 1, padding: "14px 0", background: "transparent", border: `1px solid ${L.border}`, color: L.textSoft, cursor: "pointer", fontSize: 11, fontWeight: 500, letterSpacing: "0.15em", fontFamily: "'Montserrat',sans-serif" }}>CANCEL</button>
        <button type="submit" disabled={loading || !stripe}
          style={{ flex: 2, padding: "14px 0", background: loading ? L.border : D.bg, border: "none", color: loading ? L.muted : D.white, cursor: loading ? "not-allowed" : "pointer", fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", fontFamily: "'Montserrat',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {loading ? <><Loader2 size={14} className="pm-spin" /> {loadingStep || "SAVING..."}</> : <><CreditCard size={14} /> SAVE CARD</>}
        </button>
      </div>
    </form>
  );
};

/* ─── MINI CARD VISUAL ─── */
const MiniCardVisual: React.FC<{ brand: string; last4: string; expMonth: number; expYear: number; isDefault: boolean }> = ({ brand, last4 }) => {
  const theme = BRAND_COLORS[brand.toLowerCase()] ?? BRAND_COLORS.default;
  return (
    <div style={{ width: 64, height: 42, borderRadius: 6, flexShrink: 0, background: theme.bg, border: `1px solid ${D.border2}`, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "6px 8px" }}>
      <div style={{ position: "absolute", top: -20, right: -20, width: 50, height: 50, borderRadius: "50%", background: `${theme.accent}15` }} />
      <div style={{ fontSize: 7, color: theme.accent, fontWeight: 700, letterSpacing: "0.1em", fontFamily: "'Montserrat',sans-serif" }}>{theme.label}</div>
      <div style={{ fontSize: 6.5, color: "rgba(255,255,255,0.6)", letterSpacing: "0.08em", fontFamily: "'Montserrat',sans-serif" }}>••{last4}</div>
    </div>
  );
};

/* ─── COPY FIELD ─── */
const CopyField: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div style={{ marginBottom: 12 }}>
      <p style={{ fontSize: 9, color: L.muted, letterSpacing: "0.2em", marginBottom: 4 }}>{label}</p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: L.surface2, border: `1px solid ${L.border}`, padding: "11px 14px" }}>
        <span style={{ fontSize: 13, color: L.text, fontFamily: "'Montserrat',sans-serif", fontWeight: 500 }}>{value}</span>
        <button onClick={handleCopy} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 0 0 12px", color: copied ? "#2d7a2d" : L.muted, display: "flex", alignItems: "center", gap: 4, fontSize: 10, letterSpacing: "0.05em", fontFamily: "'Montserrat',sans-serif", transition: "color 0.2s" }}>
          {copied ? <><Check size={12} /> COPIED</> : <><Copy size={12} /> COPY</>}
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════ */
type Tab = "cards" | "mobile" | "google-pay" | "bank-local" | "bank-intl";

const PaymentMethods: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const { user }   = useSelector((s: RootState) => s.auth);
  const userId     = paramUserId ?? user?.userId ?? "";
  const userEmail  = user?.email ?? user?.emailAddress ?? "";
  const userFullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const { savedCards } = useSelector((s: RootState) => s.transactions);

  const [activeTab, setActiveTab]         = useState<Tab>("cards");
  const [showAddCard, setShowAddCard]     = useState(false);
  const [loaded, setLoaded]               = useState(false);
  const [deletingId, setDeletingId]       = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [toast, setToast]                 = useState<{ success: boolean; message: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  useEffect(() => {
    if (userId) dispatch(listenToUserSavedCards(userId));
    return () => { dispatch(cleanupWalletListeners()); };
  }, [userId, dispatch]);

  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  const showToast = (success: boolean, message: string) => {
    setToast({ success, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleDelete = async (cardId: string) => {
    setDeletingId(cardId); setConfirmDelete(null);
    try {
      const { data } = await axios.post(`${API}/deleteSavedCard`, { userId, cardId });
      if (data.success) showToast(true, "Card removed successfully");
      else throw new Error(data.error || "Failed to delete card");
    } catch (err: any) {
      showToast(false, err.response?.data?.error || err.message || "Could not delete card");
    } finally { setDeletingId(null); }
  };

  const handleSetDefault = async (cardId: string) => {
    setSettingDefaultId(cardId);
    try {
      const { data } = await axios.post(`${API}/setDefaultCard`, { userId, cardId });
      if (data.success) showToast(true, "Default card updated");
      else throw new Error(data.error || "Failed to update default");
    } catch (err: any) {
      showToast(false, err.response?.data?.error || err.message || "Could not update default card");
    } finally { setSettingDefaultId(null); }
  };

  const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: "cards",      label: "Saved Cards",   icon: <CreditCard size={14} strokeWidth={1.5} /> },
    { id: "mobile",     label: "Mobile Money",  icon: <Phone size={14} strokeWidth={1.5} /> },
    { id: "google-pay", label: "Google Pay",    icon: <Smartphone size={14} strokeWidth={1.5} /> },
    { id: "bank-local", label: "Local Transfer",icon: <Building2 size={14} strokeWidth={1.5} /> },
    { id: "bank-intl",  label: "International", icon: <Globe size={14} strokeWidth={1.5} /> },
  ];

  return (
    <div style={{ minHeight: "100vh", background: L.bg, color: L.text, fontFamily: "'Montserrat','Cormorant Garamond',system-ui,sans-serif" }}>
      <style>{PM_CSS}</style>

      {/* HEADER */}
      <header style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(0,0,0,0.95)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${D.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", height: 58 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: D.offwhite, display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.05em" }}>
            <ArrowLeft size={16} strokeWidth={1.5} />
            <span className="pm-back-label">BACK</span>
          </button>
        </div>
        <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
          <span style={{ color: D.white, fontWeight: 300, letterSpacing: "0.35em", fontFamily: "'Cormorant Garamond',serif", fontSize: 15 }}>COSMOOLA</span>
        </div>
        <img src={logo} alt="C" style={{ width: 28, height: 28, borderRadius: 0, objectFit: "cover" }} />
      </header>

      <div className="pm-container">
        <div className="pm-main">

          {/* TITLE */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }} transition={{ duration: 0.4 }} style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 28, fontWeight: 300, color: L.text, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.06em", margin: 0 }}>Payment Methods</h1>
            <p style={{ fontSize: 12, color: L.textSoft, marginTop: 6, letterSpacing: "0.05em" }}>Manage how you fund your Cosmoola wallet</p>
          </motion.div>

          {/* TABS */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }} transition={{ duration: 0.4, delay: 0.05 }}
            style={{ display: "flex", gap: 0, borderBottom: `1px solid ${L.border}`, marginBottom: 24, overflowX: "auto" }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "14px 16px",
                  background: "transparent", border: "none",
                  borderBottom: activeTab === tab.id ? `2px solid ${L.text}` : "2px solid transparent",
                  color: activeTab === tab.id ? L.text : L.muted, cursor: "pointer",
                  fontSize: 10, fontWeight: 600, letterSpacing: "0.13em",
                  fontFamily: "'Montserrat',sans-serif", whiteSpace: "nowrap",
                  transition: "all 0.2s", marginBottom: -1,
                }}>
                {tab.icon}{tab.label.toUpperCase()}
              </button>
            ))}
          </motion.div>

          <AnimatePresence mode="wait">

            {/* ── SAVED CARDS ── */}
            {activeTab === "cards" && (
              <motion.div key="cards" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                {savedCards.length === 0 && !showAddCard && (
                  <div style={{ background: L.surface, border: `1px solid ${L.border}`, padding: "48px 28px", textAlign: "center", marginBottom: 20 }}>
                    <div style={{ width: 56, height: 56, background: L.surface2, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                      <CreditCard size={24} color={L.muted} strokeWidth={1.5} />
                    </div>
                    <p style={{ fontSize: 15, color: L.textSoft, fontFamily: "'Cormorant Garamond',serif", marginBottom: 6 }}>No cards saved yet</p>
                    <p style={{ fontSize: 11, color: L.muted, marginBottom: 24, lineHeight: 1.7 }}>Add a debit or credit card to top up your wallet instantly</p>
                    <button onClick={() => setShowAddCard(true)} style={{ background: D.bg, color: D.white, border: "none", padding: "12px 28px", cursor: "pointer", fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", fontFamily: "'Montserrat',sans-serif", display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <Plus size={14} /> ADD A CARD
                    </button>
                  </div>
                )}
                {savedCards.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                    {savedCards.map((card, i) => (
                      <motion.div key={card.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                        style={{ display: "flex", alignItems: "center", gap: 14, padding: "18px 20px", background: card.isDefault ? L.surface : L.bg, border: `${card.isDefault ? "2px" : "1px"} solid ${card.isDefault ? L.text : L.border}` }}>
                        <MiniCardVisual brand={card.brand} last4={card.last4} expMonth={card.expMonth} expYear={card.expYear} isDefault={card.isDefault} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 500, color: L.text }}>{card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} •••• {card.last4}</div>
                          <div style={{ fontSize: 10, color: L.muted, marginTop: 3 }}>
                            Expires {String(card.expMonth).padStart(2, "0")}/{card.expYear}
                            {card.isDefault && <span style={{ marginLeft: 10, fontSize: 8, fontWeight: 700, letterSpacing: "0.15em", background: D.bg, color: D.gold, padding: "2px 7px" }}>DEFAULT</span>}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          {!card.isDefault && (
                            <motion.button whileTap={{ scale: 0.92 } as any} onClick={() => handleSetDefault(card.id)} disabled={settingDefaultId === card.id}
                              style={{ background: "none", border: `1px solid ${L.border}`, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: L.muted }}>
                              {settingDefaultId === card.id ? <Loader2 size={13} className="pm-spin" /> : <Star size={13} />}
                            </motion.button>
                          )}
                          <motion.button whileTap={{ scale: 0.92 } as any} onClick={() => setConfirmDelete(card.id)} disabled={deletingId === card.id}
                            style={{ background: "none", border: `1px solid ${L.border}`, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#B22222" }}>
                            {deletingId === card.id ? <Loader2 size={13} className="pm-spin" /> : <Trash2 size={13} />}
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
                {savedCards.length > 0 && !showAddCard && (
                  <motion.button whileTap={{ scale: 0.97 } as any} onClick={() => setShowAddCard(true)}
                    style={{ width: "100%", padding: "14px 0", background: "transparent", border: `1px dashed ${L.border2}`, cursor: "pointer", color: L.textSoft, fontSize: 11, fontWeight: 500, letterSpacing: "0.15em", fontFamily: "'Montserrat',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <Plus size={14} /> ADD ANOTHER CARD
                  </motion.button>
                )}
                <AnimatePresence>
                  {showAddCard && (
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}
                      style={{ background: L.surface, border: `1px solid ${L.border}`, padding: "28px 24px", marginTop: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 600, color: L.text, letterSpacing: "0.15em" }}>ADD NEW CARD</p>
                          <p style={{ fontSize: 10, color: L.muted, marginTop: 3 }}>Visa, Mastercard, Amex accepted</p>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          {["visa","mastercard","amex"].map(b => (
                            <div key={b} style={{ width: 32, height: 22, borderRadius: 3, background: BRAND_COLORS[b].bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <span style={{ fontSize: 6, color: BRAND_COLORS[b].accent, fontWeight: 800 }}>{BRAND_COLORS[b].label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Elements stripe={stripePromise}>
                        <CardForm userId={userId} email={userEmail} name={userFullName}
                          onSuccess={msg => { showToast(true, msg); setShowAddCard(false); }}
                          onError={msg => showToast(false, msg)}
                          onClose={() => setShowAddCard(false)} />
                      </Elements>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* ── MOBILE MONEY ── */}
            {activeTab === "mobile" && (
              <motion.div key="mobile" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                <MobileMoney userId={userId} />
              </motion.div>
            )}

            {/* ── GOOGLE PAY ── */}
            {activeTab === "google-pay" && (
              <motion.div key="gpay" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                <div style={{ background: L.surface, border: `1px solid ${L.border}`, padding: "36px 28px", marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
                    <div style={{ background: "#000", borderRadius: 6, padding: "14px 32px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", boxShadow: "0 2px 12px rgba(0,0,0,0.15)", border: "1px solid rgba(255,255,255,0.05)" }}>
                      <svg width="18" height="18" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      <span style={{ color: "#fff", fontSize: 15, fontWeight: 500, fontFamily: "'Montserrat',sans-serif" }}>Pay</span>
                    </div>
                  </div>
                  <div style={{ textAlign: "center", marginBottom: 28 }}>
                    <p style={{ fontSize: 13, color: L.textSoft, lineHeight: 1.8, maxWidth: 420, margin: "0 auto" }}>Top up your Cosmoola wallet instantly using Google Pay. Your cards saved in Google Wallet are used — no manual entry required.</p>
                  </div>
                  {[{ icon: "⚡", title: "Instant credit", desc: "Funds appear in your wallet immediately" }, { icon: "🔒", title: "Tokenised & secure", desc: "Google Pay never shares your real card number" }, { icon: "📱", title: "Biometric auth", desc: "Confirm with Face ID, fingerprint, or PIN" }].map(f => (
                    <div key={f.title} style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "16px 0", borderBottom: `1px solid ${L.border}` }}>
                      <div style={{ fontSize: 20, flexShrink: 0, width: 36, textAlign: "center" }}>{f.icon}</div>
                      <div><p style={{ fontSize: 12, fontWeight: 600, color: L.text, marginBottom: 3 }}>{f.title}</p><p style={{ fontSize: 11, color: L.muted, lineHeight: 1.6 }}>{f.desc}</p></div>
                    </div>
                  ))}
                  <div style={{ marginTop: 20, padding: "14px 16px", background: "#fffbf0", border: "1px solid #f0e8c8", display: "flex", alignItems: "center", gap: 10 }}>
                    <AlertCircle size={14} color="#b8860b" />
                    <span style={{ fontSize: 10, color: "#b8860b", lineHeight: 1.6 }}>Google Pay top-up is currently in <strong>beta</strong>. Only available on Android & Chrome on desktop.</span>
                  </div>
                  <button style={{ width: "100%", marginTop: 20, padding: "14px 0", background: D.bg, border: "none", color: D.white, cursor: "pointer", fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", fontFamily: "'Montserrat',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <Smartphone size={14} /> ENABLE GOOGLE PAY
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── LOCAL BANK TRANSFER ── */}
            {activeTab === "bank-local" && (
              <motion.div key="bank-local" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                <div style={{ background: L.surface, border: `1px solid ${L.border}`, padding: "28px 24px", marginBottom: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${L.border}` }}>
                    <div style={{ width: 44, height: 44, background: D.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Building2 size={20} color={D.gold} strokeWidth={1.5} /></div>
                    <div><p style={{ fontSize: 13, fontWeight: 600, color: L.text }}>Local Bank Transfer</p><p style={{ fontSize: 10, color: L.muted, marginTop: 2 }}>SEPA Credit Transfer · EUR only · 1–2 business days</p></div>
                  </div>
                  <p style={{ fontSize: 11, color: L.textSoft, lineHeight: 1.8, marginBottom: 24 }}>Send a bank transfer from any EU/EEA bank. Always include your <strong>User ID as the reference</strong>.</p>
                  <CopyField label="ACCOUNT HOLDER" value={BANK_DETAILS.local.accountHolder} />
                  <CopyField label="IBAN" value={BANK_DETAILS.local.iban} />
                  <CopyField label="BIC / SWIFT" value={BANK_DETAILS.local.bic} />
                  <CopyField label="BANK" value={BANK_DETAILS.local.bankName} />
                  <div style={{ background: "#f0f9f0", border: "1px solid #d4ecd4", padding: "14px 16px", marginTop: 8 }}>
                    <p style={{ fontSize: 10, color: "#2d7a2d", fontWeight: 600, marginBottom: 4 }}>PAYMENT REFERENCE</p>
                    <p style={{ fontSize: 11, color: "#2d7a2d", lineHeight: 1.7 }}>Include your <strong>User ID ({userId || "your-user-id"})</strong> in the reference field.</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── INTERNATIONAL WIRE ── */}
            {activeTab === "bank-intl" && (
              <motion.div key="bank-intl" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                <div style={{ background: L.surface, border: `1px solid ${L.border}`, padding: "28px 24px", marginBottom: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${L.border}` }}>
                    <div style={{ width: 44, height: 44, background: D.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Globe size={20} color={D.gold} strokeWidth={1.5} /></div>
                    <div><p style={{ fontSize: 13, fontWeight: 600, color: L.text }}>International Wire Transfer</p><p style={{ fontSize: 10, color: L.muted, marginTop: 2 }}>SWIFT · All currencies · 3–5 business days</p></div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 24 }}>
                    {[{ label: "MIN AMOUNT", value: "€100" }, { label: "ARRIVAL", value: "3–5 days" }, { label: "FX FEE", value: "0.5%" }].map(s => (
                      <div key={s.label} style={{ background: L.surface2, border: `1px solid ${L.border}`, padding: "14px 12px", textAlign: "center" }}>
                        <p style={{ fontSize: 9, color: L.muted, letterSpacing: "0.2em", marginBottom: 6 }}>{s.label}</p>
                        <p style={{ fontSize: 16, fontWeight: 300, color: L.text, fontFamily: "'Cormorant Garamond',serif" }}>{s.value}</p>
                      </div>
                    ))}
                  </div>
                  <CopyField label="BENEFICIARY NAME" value={BANK_DETAILS.international.accountHolder} />
                  <CopyField label="IBAN" value={BANK_DETAILS.international.iban} />
                  <CopyField label="SWIFT / BIC" value={BANK_DETAILS.international.bic} />
                  <p style={{ fontSize: 9, color: L.muted, letterSpacing: "0.2em", marginTop: 20, marginBottom: 14 }}>CORRESPONDENT BANK (USD / GBP)</p>
                  <CopyField label="CORRESPONDENT BANK" value={BANK_DETAILS.international.correspondentBank} />
                  <CopyField label="CORRESPONDENT BIC" value={BANK_DETAILS.international.correspondentBIC} />
                  <div style={{ background: "#fffbf0", border: "1px solid #f0e8c8", padding: "14px 16px", marginTop: 8 }}>
                    <p style={{ fontSize: 10, color: "#b8860b", fontWeight: 600, marginBottom: 4 }}>REFERENCE — REQUIRED</p>
                    <p style={{ fontSize: 11, color: "#b8860b", lineHeight: 1.7 }}>Include <strong>INTL-{userId || "YOUR-USER-ID"}</strong> in the payment reference field.</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* SIDEBAR */}
        <div className="pm-sidebar">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }} transition={{ duration: 0.4, delay: 0.2 }} style={{ position: "sticky", top: 80 }}>
            <div style={{ background: L.surface, border: `1px solid ${L.border}`, padding: "24px 20px", marginBottom: 16 }}>
              <p style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", marginBottom: 16 }}>WALLET STATUS</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 11, color: L.muted }}>Saved cards</span><span style={{ fontSize: 13, fontWeight: 600, color: L.text }}>{savedCards.length}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 11, color: L.muted }}>Default card</span><span style={{ fontSize: 11, color: L.text }}>{savedCards.find(c => c.isDefault) ? `•••• ${savedCards.find(c => c.isDefault)!.last4}` : "—"}</span></div>
              </div>
              <div style={{ height: 1, background: L.border, margin: "16px 0" }} />
              {([{ label: "Add Money", path: `/AddMoney/${userId}` }, { label: "Transaction History", path: `/Transactions/${userId}` }] as const).map(item => (
                <button key={item.label} onClick={() => navigate(item.path)}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", background: "none", border: "none", borderBottom: `1px solid ${L.border}`, cursor: "pointer", color: L.text, fontSize: 11, fontFamily: "inherit", letterSpacing: "0.05em" }}>
                  {item.label}<ChevronRight size={12} color={L.muted} />
                </button>
              ))}
            </div>
            <div style={{ background: D.bg, border: `1px solid ${D.border}`, padding: "20px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <Shield size={14} color={D.gold} />
                <span style={{ fontSize: 10, color: D.gold, letterSpacing: "0.15em", fontWeight: 600 }}>SECURITY</span>
              </div>
              {["256-bit TLS encryption", "PCI DSS Level 1 compliant", "Card details stored by Stripe", "3D Secure authentication", "Real-time fraud monitoring"].map(item => (
                <div key={item} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: D.gold, flexShrink: 0 }} />
                  <span style={{ fontSize: 10, color: D.muted }}>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* DELETE CONFIRM MODAL */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 20px" }}
            onClick={() => setConfirmDelete(null)}>
            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              onClick={e => e.stopPropagation()}
              style={{ background: L.bg, border: `1px solid ${L.border}`, padding: "32px 28px", maxWidth: 400, width: "100%" }}>
              <div style={{ width: 48, height: 48, background: "#fff0f0", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><Trash2 size={20} color="#B22222" /></div>
              <h3 style={{ textAlign: "center", fontSize: 18, fontWeight: 400, fontFamily: "'Cormorant Garamond',serif", color: L.text, marginBottom: 8 }}>Remove this card?</h3>
              <p style={{ textAlign: "center", fontSize: 11, color: L.muted, lineHeight: 1.7, marginBottom: 24 }}>This card will be permanently removed and detached from Stripe. This cannot be undone.</p>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setConfirmDelete(null)} style={{ flex: 1, padding: "12px 0", background: "transparent", border: `1px solid ${L.border}`, color: L.textSoft, cursor: "pointer", fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", fontFamily: "'Montserrat',sans-serif" }}>CANCEL</button>
                <button onClick={() => handleDelete(confirmDelete)} style={{ flex: 1, padding: "12px 0", background: "#B22222", border: "none", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", fontFamily: "'Montserrat',sans-serif" }}>REMOVE</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* TOAST */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }} transition={{ duration: 0.3 }}
            style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.success ? D.bg : "#B22222", color: D.white, padding: "16px 28px", display: "flex", alignItems: "center", gap: 12, border: toast.success ? `1px solid ${D.gold}` : "1px solid #D44", zIndex: 9999, maxWidth: 500, width: "90%" }}>
            {toast.success ? <CheckCircle size={20} color={D.gold} /> : <AlertCircle size={20} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{toast.success ? "Success" : "Error"}</div>
              <div style={{ fontSize: 11, color: toast.success ? D.muted : "rgba(255,255,255,0.7)", marginTop: 2 }}>{toast.message}</div>
            </div>
            <button onClick={() => setToast(null)} style={{ background: "none", border: "none", cursor: "pointer", color: D.white, padding: 4 }}><X size={16} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FOOTER */}
      <footer style={{ background: D.bg, borderTop: `1px solid ${D.border}`, padding: "28px 24px", marginTop: 40 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ fontSize: 11, color: D.muted }}>© 2025 – {new Date().getFullYear()} Cosmoola Technologies</div>
          <div style={{ display: "flex", gap: 20 }}>
            {["privacy","terms","support"].map(p => (
              <span key={p} onClick={() => navigate(`/${p}`)} style={{ fontSize: 11, color: D.muted, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>{p.charAt(0).toUpperCase() + p.slice(1)}</span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PaymentMethods;

const PM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:#fff}button{font-family:inherit}input{font-family:inherit}
  ::-webkit-scrollbar{width:0;height:0}
  input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none}
  input[type=number]{-moz-appearance:textfield}
  @keyframes pm-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  .pm-spin{animation:pm-spin 1s linear infinite}
  .pm-container{max-width:1200px;margin:0 auto;padding:32px 24px 0;display:grid;grid-template-columns:1fr 320px;gap:28px;align-items:start}
  .pm-main{min-width:0}.pm-sidebar{min-width:0}
  @media(max-width:900px){
    .pm-container{grid-template-columns:1fr!important;gap:0!important;padding:20px 16px 0!important}
    .pm-sidebar{display:none}.pm-back-label{display:none}
  }
  @media(min-width:901px){.pm-back-label{display:inline}}
`;